import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from './ui/pagination';
import { Settings, AlertCircle, CheckCircle2, RefreshCw, Info, Sparkles, Loader2, FileText, XCircle, CheckCircle } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { DraggableConfigItem } from './DraggableConfigItem';
import { SOWDialog } from './SOWDialog';
import type { ConfigItem, CompanyConfigOverride, ConfigPhase, ConfigCategory, SOW, ChangeOrder } from '../types';

interface CompanyConfigOverridesManagerProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  hideSOWs?: boolean;
}

const CATEGORY_LABELS: Record<ConfigCategory, string> = {
  'sla': 'Service Level Agreements',
  'queue': 'Queues',
  'approval': 'Approval Flows',
  'asset': 'Asset Management',
  'automation': 'Automation Rules',
  'notification': 'Notifications',
  'email': 'Email Configuration',
  'portal': 'Customer Portal',
  'request-type-group': 'Request Type Groups',
  'knowledge-base': 'Knowledge Base',
  'integration': 'Integrations',
  'csat': 'Customer Satisfaction (CSAT)',
  'dashboard': 'Reports & Dashboards',
  'field-configuration': 'Field Configurations',
  'service-catalog': 'Service Catalog',
  'team-settings': 'Team & Agent Settings'
};

const PHASE_LABELS = {
  1: 'Phase 1 (Core)',
  2: 'Phase 2 (Automation)',
  3: 'Phase 3 (Portal)',
  4: 'Phase 4 (Advanced)',
  5: 'Future Consideration'
};

const PHASE_COLORS = {
  1: 'bg-blue-100 text-blue-800 border-blue-300',
  2: 'bg-green-100 text-green-800 border-green-300',
  3: 'bg-purple-100 text-purple-800 border-purple-300',
  4: 'bg-orange-100 text-orange-800 border-orange-300',
  5: 'bg-gray-100 text-gray-800 border-gray-300'
};

interface ConfigItemWithOverride extends ConfigItem {
  override?: CompanyConfigOverride;
  effectivePhase: ConfigPhase;
  effectiveEnabled: boolean;
}

export function CompanyConfigOverridesManager({ 
  accessToken, 
  companyId, 
  companyName,
  hideSOWs = false
}: CompanyConfigOverridesManagerProps) {
  const [configItems, setConfigItems] = useState<ConfigItemWithOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ConfigItemWithOverride | null>(null);
  const [filterPhase, setFilterPhase] = useState<ConfigPhase | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<ConfigCategory | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'overridden' | 'default'>('all');
  const [assignableUsers, setAssignableUsers] = useState<any[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // SOW Analysis state
  const [sows, setSOWs] = useState<SOW[]>([]);
  const [sowAnalyzing, setSowAnalyzing] = useState(false);
  const [sowSuggestions, setSowSuggestions] = useState<any[]>([]);
  const [sowDialogOpen, setSowDialogOpen] = useState(false);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const [createSOWDialogOpen, setCreateSOWDialogOpen] = useState(false);
  const [selectedSOWsForAnalysis, setSelectedSOWsForAnalysis] = useState<string[]>([]);

  // Form state
  const [formPhase, setFormPhase] = useState<ConfigPhase>(1);
  const [formEnabled, setFormEnabled] = useState(true);
  const [formNotes, setFormNotes] = useState('');
  const [formAssignedTo, setFormAssignedTo] = useState<string | null>(null);

  useEffect(() => {
    loadConfigItems();
    loadAssignableUsers();
    if (!hideSOWs) {
      loadSOWs();
    }
  }, [companyId, hideSOWs]);

  const loadAssignableUsers = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/assignable-users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAssignableUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading assignable users:', error);
    }
  };

  const loadSOWs = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSOWs(data.sows || []);
      } else {
        setSOWs([]);
      }
    } catch (error) {
      console.error('Error loading SOWs:', error);
      setSOWs([]);
    }
  };

  const handleSaveSOW = async (sowData: any) => {
    try {
      const newSOW: SOW = {
        ...sowData,
        id: Date.now().toString(),
        changeOrders: []
      };

      // Get current company data
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      let companyData: any = {};
      if (response.ok) {
        companyData = await response.json();
      }

      // Add new SOW to company data
      const updatedSOWs = [...(companyData.sows || []), newSOW];
      
      // Save updated company data
      const saveResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ 
            ...companyData,
            sows: updatedSOWs, 
            companyId 
          }),
        }
      );

      if (saveResponse.ok) {
        setSOWs(updatedSOWs);
        setCreateSOWDialogOpen(false);
        alert('SOW added. You can now analyze it.');
        await loadSOWs();
      } else {
        alert('Error saving SOW. Please try again.');
      }
    } catch (error) {
      console.error('Error saving SOW:', error);
      alert('Error saving SOW. Please try again.');
    }
  };

  const handleAnalyzeSOW = async () => {
    if (selectedSOWsForAnalysis.length === 0) {
      alert('Please select at least one SOW or Change Order to analyze');
      return;
    }

    setSowAnalyzing(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/analyze-sow/${companyId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sowIds: selectedSOWsForAnalysis
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        
        // Show user-friendly error message
        const errorMsg = error.userMessage || error.error || 'Failed to analyze SOW';
        
        if (error.isQuotaError) {
          // Gracefully inform the user without intrusive alerts
          console.warn('OpenAI API quota exceeded. SOW analysis feature is currently unavailable.');
          alert(
            `AI Analysis Unavailable\n\n` +
            `The OpenAI API quota has been exceeded. SOW analysis is currently unavailable.\n\n` +
            `You can continue working by manually configuring the phase settings below. ` +
            `Contact your system administrator if you need AI analysis capabilities.`
          );
        } else {
          console.error(`SOW analysis error: ${errorMsg}`);
          alert(`Unable to analyze SOW: ${errorMsg}\n\nYou can manually configure phases below.`);
        }
        
        return; // Don't throw, just return
      }

      const data = await response.json();
      setSowSuggestions(data.suggestions || []);
      setSowDialogOpen(true);
    } catch (error: any) {
      console.error('Error analyzing SOW:', error);
      // Silent failure - user can still work manually
    } finally {
      setSowAnalyzing(false);
    }
  };

  const handleApplySuggestions = async () => {
    setApplyingSuggestions(true);
    try {
      // Apply each suggestion as a company override
      for (const suggestion of sowSuggestions) {
        const override = {
          configItemId: suggestion.itemId,
          phase: suggestion.phase,
          enabled: suggestion.enabled,
        };

        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}/override`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ override }),
          }
        );
      }

      alert(`Successfully applied ${sowSuggestions.length} configuration suggestions.`);
      setSowDialogOpen(false);
      setSowSuggestions([]);
      setSelectedSOWsForAnalysis([]);
      await loadConfigItems();
    } catch (error) {
      console.error('Error applying suggestions:', error);
      alert('Error applying suggestions. Please try again.');
    } finally {
      setApplyingSuggestions(false);
    }
  };

  const loadConfigItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConfigItems(data.items || []);
      }
    } catch (error) {
      console.error('Error loading config items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingItem) return;

    const hasChanges = formPhase !== editingItem.defaultPhase || !formEnabled;
    
    // Find the assigned user details
    const assignedUser = formAssignedTo ? assignableUsers.find(u => u.userId === formAssignedTo) : null;
    
    const override: CompanyConfigOverride = {
      configItemId: editingItem.id,
      phase: hasChanges ? formPhase : undefined,
      enabled: formEnabled,
      notes: formNotes.trim() || undefined,
      assignedTo: assignedUser || undefined
    };

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}/override`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ override }),
        }
      );

      if (response.ok) {
        await loadConfigItems();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving config override:', error);
    }
  };

  const handleResetToDefault = async (itemId: string) => {
    if (!confirm('Reset this item to the system default? This will remove any company-specific overrides.')) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}/override/${itemId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        await loadConfigItems();
      }
    } catch (error) {
      console.error('Error resetting config item:', error);
    }
  };

  const handleMoveItem = async (itemId: string, fromPhase: ConfigPhase, toPhase: ConfigPhase, newIndex: number) => {
    const item = configItems.find(i => i.id === itemId);
    if (!item) return;

    // Create or update override with new phase
    const override: CompanyConfigOverride = {
      configItemId: item.id,
      phase: toPhase,
      enabled: item.effectiveEnabled,
      notes: item.override?.notes
    };

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}/override`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ override }),
        }
      );

      if (response.ok) {
        await loadConfigItems();
      }
    } catch (error) {
      console.error('Error moving config item:', error);
      await loadConfigItems();
    }
  };

  const handleReorderItem = async (itemId: string, fromIndex: number, toIndex: number, phase: ConfigPhase) => {
    // Get items in the current phase
    const phaseItems = configItems.filter(i => i.effectivePhase === phase);
    const otherItems = configItems.filter(i => i.effectivePhase !== phase);
    
    // Reorder within the phase
    const reorderedPhaseItems = [...phaseItems];
    const [movedItem] = reorderedPhaseItems.splice(fromIndex, 1);
    reorderedPhaseItems.splice(toIndex, 0, movedItem);
    
    // Combine and update
    const updatedItems = [...otherItems, ...reorderedPhaseItems];
    setConfigItems(updatedItems);
    
    // Note: Order is stored implicitly by the order items are returned
    // We could add an explicit order field if needed for persistence
  };

  const handleEdit = (item: ConfigItemWithOverride) => {
    setEditingItem(item);
    setFormPhase(item.effectivePhase);
    setFormEnabled(item.effectiveEnabled);
    setFormNotes(item.override?.notes || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormPhase(1);
    setFormEnabled(true);
    setFormNotes('');
  };

  const filteredItems = configItems.filter(item => {
    if (filterPhase !== 'all' && item.effectivePhase !== filterPhase) return false;
    if (filterCategory !== 'all' && item.category !== filterCategory) return false;
    if (filterStatus === 'overridden' && !item.override) return false;
    if (filterStatus === 'default' && item.override) return false;
    return true;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterPhase, filterCategory, filterStatus]);

  // Calculate pagination
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  const itemsByPhase = {
    1: paginatedItems.filter(i => i.effectivePhase === 1),
    2: paginatedItems.filter(i => i.effectivePhase === 2),
    3: paginatedItems.filter(i => i.effectivePhase === 3),
    4: paginatedItems.filter(i => i.effectivePhase === 4),
    5: paginatedItems.filter(i => i.effectivePhase === 5)
  };

  const overriddenCount = configItems.filter(i => i.override).length;
  const disabledCount = configItems.filter(i => !i.effectiveEnabled).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading configuration...</p>
      </div>
    );
  }

  // Determine if we should show SOW setup prominently (new company with few overrides)
  const isNewSetup = overriddenCount < 5;

  // Get all SOWs and Change Orders for selection
  const sowsAndCOs: Array<{id: string; label: string; type: 'sow' | 'co'}> = [];
  sows.forEach(sow => {
    sowsAndCOs.push({ id: sow.id, label: sow.sowId, type: 'sow' });
    sow.changeOrders?.forEach((co, index) => {
      sowsAndCOs.push({ id: `${sow.id}:${co.id}`, label: `CO${index + 1} (${co.changeOrderId})`, type: 'co' });
    });
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-6">
        <div>
          <h3>Configuration for {companyName}</h3>
          <p className="text-muted-foreground">
            Customize which configuration items apply to this company and in which phase they should be implemented.
          </p>
        </div>

        {/* SOW-Based Configuration Setup - prominent when company is being set up */}
        {!hideSOWs && isNewSetup && sows.length === 0 ? (
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Quick Setup: Add SOW & Auto-Configure
              </CardTitle>
              <CardDescription>
                Start by adding a Statement of Work, then use AI to automatically configure your implementation phases based on the SOW scope.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>No SOW found for this company.</strong> Add an SOW first, then AI can analyze it to suggest which configuration items should be enabled and in which phases.
                </AlertDescription>
              </Alert>
              <Button onClick={() => setCreateSOWDialogOpen(true)}>
                <FileText className="mr-2 h-4 w-4" />
                Add Statement of Work
              </Button>
            </CardContent>
          </Card>
        ) : !hideSOWs ? (
          <Card className={isNewSetup ? "bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                SOW-Based Configuration Analysis
              </CardTitle>
              <CardDescription>
                {isNewSetup 
                  ? 'Use AI to automatically configure implementation phases based on your Statement of Work and Change Orders'
                  : 'Re-run AI analysis to update configuration when scope changes via Change Orders'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {sows.length > 0 && (
                <>
                  <div>
                    <Label>Select SOWs and Change Orders to Analyze</Label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                      {sowsAndCOs.map((item) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox
                            id={item.id}
                            checked={selectedSOWsForAnalysis.includes(item.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedSOWsForAnalysis([...selectedSOWsForAnalysis, item.id]);
                              } else {
                                setSelectedSOWsForAnalysis(selectedSOWsForAnalysis.filter(id => id !== item.id));
                              }
                            }}
                          />
                          <Label htmlFor={item.id} className="cursor-pointer flex items-center gap-2">
                            {item.type === 'sow' ? (
                              <Badge variant="default">{item.label}</Badge>
                            ) : (
                              <Badge variant="secondary">{item.label}</Badge>
                            )}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedSOWsForAnalysis(sowsAndCOs.map(item => item.id))}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedSOWsForAnalysis([])}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAnalyzeSOW}
                      disabled={selectedSOWsForAnalysis.length === 0 || sowAnalyzing}
                      variant="outline"
                    >
                      {sowAnalyzing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Analyzing with AI...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          AI Analyze (Optional)
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setCreateSOWDialogOpen(true)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Add SOW/Change Order
                    </Button>
                  </div>
                </>
              )}

              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-900">
                  <strong>Optional Feature:</strong> AI analysis can automatically suggest configuration items based on your SOW documents. 
                  However, this feature is <strong>completely optional</strong> and requires an active OpenAI API subscription.
                  <br /><br />
                  <strong>You can skip this step</strong> and manually configure all phase settings using the drag-and-drop interface below.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        ) : null}

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Drag and drop</strong> configuration items to move them between phases or reorder them within a phase. 
            You can also use the "Customize" button to adjust settings or disable items.
          </AlertDescription>
        </Alert>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Total Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{configItems.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Filtered</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-purple-600">{filteredItems.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Customized</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-blue-600">{overriddenCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Disabled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-muted-foreground">{disabledCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="w-56">
          <Label>Filter by Phase</Label>
          <Select value={filterPhase.toString()} onValueChange={(v) => setFilterPhase(v as ConfigPhase | 'all')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Phases</SelectItem>
              <SelectItem value="1">Phase 1 (Core)</SelectItem>
              <SelectItem value="2">Phase 2 (Automation)</SelectItem>
              <SelectItem value="3">Phase 3 (Portal)</SelectItem>
              <SelectItem value="4">Phase 4 (Advanced)</SelectItem>
              <SelectItem value="5">Future Consideration</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <Label>Filter by Category</Label>
          <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v as any)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <Label>Filter by Status</Label>
          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="overridden">Customized Only</SelectItem>
              <SelectItem value="default">Default Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Items by Phase */}
      <div className="space-y-6">
        {([1, 2, 3, 4, 5] as ConfigPhase[]).map(phase => (
          <Card key={phase}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge className={PHASE_COLORS[phase]}>
                  {PHASE_LABELS[phase]}
                </Badge>
                <span className="text-muted-foreground">
                  ({itemsByPhase[phase].length} items)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {itemsByPhase[phase].length === 0 ? (
                <p className="text-muted-foreground">No items in this phase. Drag items here to move them to this phase.</p>
              ) : (
                <div className="space-y-3">
                  {itemsByPhase[phase].map((item, index) => (
                    <DraggableConfigItem
                      key={item.id}
                      item={item}
                      index={index}
                      phase={phase}
                      onMoveItem={handleMoveItem}
                      onReorderItem={handleReorderItem}
                      showDefaultPhase={true}
                      renderActions={(item) => (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                            <Settings className="h-4 w-4 mr-1" />
                            Customize
                          </Button>
                          {item.override && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleResetToDefault(item.id)}
                              title="Reset to default"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                // Show first page, last page, current page, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setCurrentPage(pageNum)}
                        isActive={currentPage === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return null;
              })}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Customize Configuration Item</DialogTitle>
            <DialogDescription>
              Adjust the phase or disable this item for {companyName}. This will not affect the system defaults.
            </DialogDescription>
          </DialogHeader>

          {editingItem && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <h4>{editingItem.name}</h4>
                <p className="text-muted-foreground">{editingItem.description}</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">{CATEGORY_LABELS[editingItem.category]}</Badge>
                  <Badge variant="outline">Default: {PHASE_LABELS[editingItem.defaultPhase]}</Badge>
                  {editingItem.coreFeature && <Badge variant="secondary">Core</Badge>}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Enable this configuration item</Label>
                  <p className="text-muted-foreground">
                    Disable if this item is not applicable to this engagement
                  </p>
                </div>
                <Switch
                  checked={formEnabled}
                  onCheckedChange={setFormEnabled}
                />
              </div>

              {formEnabled && (
                <div>
                  <Label>Implementation Phase</Label>
                  <Select value={formPhase.toString()} onValueChange={(v) => setFormPhase(parseInt(v) as any)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Phase 1 (Core)</SelectItem>
                      <SelectItem value="2">Phase 2 (Automation)</SelectItem>
                      <SelectItem value="3">Phase 3 (Portal)</SelectItem>
                      <SelectItem value="4">Phase 4 (Advanced)</SelectItem>
                      <SelectItem value="5">Future Consideration</SelectItem>
                    </SelectContent>
                  </Select>
                  {formPhase !== editingItem.defaultPhase && (
                    <p className="text-sm text-blue-700 mt-1">
                      This will move the item from {PHASE_LABELS[editingItem.defaultPhase]} to {PHASE_LABELS[formPhase]}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Why was this changed? Any special considerations..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Customization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SOW Dialog for creating new SOWs */}
      <SOWDialog
        open={createSOWDialogOpen}
        onClose={() => setCreateSOWDialogOpen(false)}
        onSave={handleSaveSOW}
      />

      {/* SOW Suggestions Dialog */}
      <Dialog open={sowDialogOpen} onOpenChange={setSowDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI-Suggested Configuration Items
            </DialogTitle>
            <DialogDescription>
              Review these AI-generated suggestions based on the selected SOW(s) and Change Order(s). You can apply them to automatically configure the company's phase setup.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {sowSuggestions.length === 0 && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  No suggestions were generated. This could mean the SOW analysis didn't find specific configuration requirements.
                </AlertDescription>
              </Alert>
            )}

            {sowSuggestions.map((suggestion) => {
              const item = configItems.find(i => i.id === suggestion.itemId);
              if (!item) return null;

              return (
                <Card key={suggestion.itemId} className={!suggestion.enabled ? 'opacity-60' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base flex items-center gap-2">
                          {suggestion.enabled ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-gray-400" />
                          )}
                          {item.name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {item.description}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Badge variant="outline">{CATEGORY_LABELS[item.category]}</Badge>
                        {suggestion.enabled && (
                          <Badge className={PHASE_COLORS[suggestion.phase]}>
                            {PHASE_LABELS[suggestion.phase]}
                          </Badge>
                        )}
                        {!suggestion.enabled && (
                          <Badge variant="secondary">Disabled</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      <strong>AI Reasoning:</strong> {suggestion.reason}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSowDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApplySuggestions}
              disabled={applyingSuggestions || sowSuggestions.length === 0}
            >
              {applyingSuggestions ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Apply {sowSuggestions.length} Suggestions
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </DndProvider>
  );
}